import React, { useEffect, useState } from 'react';
import { XIcon, CheckIcon } from 'lucide-react';
import Button from './Button';
import { sendWebhookData } from '../services/webhook';
import { trackEvent } from '../services/analytics';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';
interface WizardProps {
  isOpen: boolean;
  onClose: () => void;
  initialAgentType?: string;
}
const Wizard: React.FC<WizardProps> = ({
  isOpen,
  onClose,
  initialAgentType = ''
}) => {
  const [formData, setFormData] = useState({
    name: '',
    catalogUrl: '',
    agentGoal: '',
    email: '',
    agentType: initialAgentType || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const {
    t
  } = useTranslation();
  // Update formData when initialAgentType changes
  useEffect(() => {
    if (initialAgentType) {
      setFormData(prev => ({
        ...prev,
        agentType: initialAgentType
      }));
    }
  }, [initialAgentType]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Track the form submission event
    trackEvent('Wizard', 'Form Submission', `Agent Type: ${formData.agentType}`);
    setIsSubmitting(true);
    try {
      // Send data to webhook
      const success = await sendWebhookData({
        ...formData,
        formType: 'trial_signup'
      });
      if (success) {
        setIsSubmitted(true);
        setTimeout(() => {
          onClose();
          navigate('/thank-you');
        }, 1000);
      } else {
        alert('There was an issue submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error during form submission:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" data-id="element-2385">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden" data-id="element-2386">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b" data-id="element-2387">
          <h3 className="text-lg font-bold text-primary" data-id="element-2388">
            {t('hero.createAssistant')}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 focus:outline-none" data-id="element-2389">
            <XIcon className="h-5 w-5" data-id="element-2390" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-2391">
          {isSubmitted ? <div className="text-center py-6" data-id="element-2392">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4" data-id="element-2393">
                <CheckIcon className="h-8 w-8 text-primary" data-id="element-2394" />
              </div>
              <h4 className="text-xl font-semibold mb-2" data-id="element-2395">
                {t('forms.thankYou')}
              </h4>
              <p className="text-gray-600" data-id="element-2396">
                We'll create your AI agent and send you the link via email soon.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2397">
              <div data-id="element-2398">
                <label className="block text-sm font-medium text-primary mb-1" data-id="element-2399">
                  {t('forms.name')} *
                </label>
                <input type="text" name="name" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-primary" placeholder="John Doe" value={formData.name} onChange={handleInputChange} required data-id="element-2400" />
              </div>
              <div data-id="element-2401">
                <label className="block text-sm font-medium text-primary mb-1" data-id="element-2402">
                  Catalog URL *
                </label>
                <input type="text" name="catalogUrl" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-primary" placeholder="https://yourstore.com/products" value={formData.catalogUrl} onChange={handleInputChange} required data-id="element-2403" />
              </div>
              <div data-id="element-2404">
                <label className="block text-sm font-medium text-primary mb-1" data-id="element-2405">
                  Agent Type *
                </label>
                <select name="agentType" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-primary" value={formData.agentType} onChange={handleInputChange} required data-id="element-2406">
                  <option value="" disabled data-id="element-2407">
                    Select agent type
                  </option>
                  <option value="website" data-id="element-2408">Website Agent</option>
                  <option value="social" data-id="element-2409">Social Media Agent</option>
                  <option value="messenger" data-id="element-2410">Messenger Agent</option>
                  <option value="call" data-id="element-2411">Call Agent</option>
                  <option value="gpt" data-id="element-2412">GPT Store</option>
                </select>
              </div>
              <div data-id="element-2413">
                <label className="block text-sm font-medium text-primary mb-1" data-id="element-2414">
                  Agent Goal *
                </label>
                <textarea name="agentGoal" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-primary" placeholder="What do you want your AI agent to help customers with?" rows={3} value={formData.agentGoal} onChange={handleInputChange} required data-id="element-2415" />
              </div>
              <div data-id="element-2416">
                <label className="block text-sm font-medium text-primary mb-1" data-id="element-2417">
                  {t('forms.email')} *
                </label>
                <input type="email" name="email" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-primary" placeholder="you@example.com" value={formData.email} onChange={handleInputChange} required data-id="element-2418" />
              </div>
              <Button variant="primary" type="submit" fullWidth onClick={() => {
            trackEvent('Wizard', 'Button Click', 'Create Your AI Agent');
          }} data-id="element-2419">
                {isSubmitting ? <span className="flex items-center justify-center" data-id="element-2420">
                    <span className="mr-2" data-id="element-2421">{t('buttons.sending')}</span>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-2422"></span>
                  </span> : t('buttons.launchAssistant')}
              </Button>
            </form>}
        </div>
      </div>
    </div>;
};
export default Wizard;